'use client';

import {
  type PointerEvent,
  type RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { SEARCH_ROW_SELECTOR } from '../shared/SearchRow';

const OPTION_SELECTOR = '[role="option"]';
/** A section's caption over its first row, kept in view with it. */
const SECTION_CAPTION_PX = 26;
const SCROLL_MARGIN_PX = 8;

export interface HighlightBox {
  top: number;
  height: number;
}

export interface ResultNavigation {
  /** The active row's box in the list content, for the sliding highlight; none without rows. */
  highlight: HighlightBox | null;
  /** The active row's option id, for the field's aria-activedescendant. */
  activeId: string | undefined;
  move: (step: 1 | -1) => void;
  /** The link under the highlight, for Enter. */
  activeLink: () => HTMLAnchorElement | null;
  handlePointerMove: (e: PointerEvent<HTMLElement>) => void;
}

function sameBox(a: HighlightBox | null, b: HighlightBox | null): boolean {
  return a?.top === b?.top && a?.height === b?.height;
}

/**
 * The arrow keys and the pointer over the panel's results, as one highlight.
 * Rows are found in the DOM by `data-search-row`, so the sections and rows
 * know nothing of it; the highlight is measured against `contentRef`, the
 * stable box the lists render into, and follows rows as answers land and the
 * recent block folds.
 */
export function useResultNavigation(
  listRef: RefObject<HTMLElement | null>,
  contentRef: RefObject<HTMLElement | null>,
  resetKey: string,
): ResultNavigation {
  const [active, setActive] = useState(0);
  const [highlight, setHighlight] = useState<HighlightBox | null>(null);
  const [activeId, setActiveId] = useState<string>();
  const scrollOnMove = useRef(false);
  const lastPointer = useRef('');

  const rows = useCallback(
    () => Array.from(contentRef.current?.querySelectorAll<HTMLElement>(SEARCH_ROW_SELECTOR) ?? []),
    [contentRef],
  );

  // A new list — another query, tab or opening — starts on its first row.
  // biome-ignore lint/correctness/useExhaustiveDependencies: resetKey is the trigger
  useEffect(() => {
    setActive(0);
  }, [resetKey]);

  useLayoutEffect(() => {
    const content = contentRef.current;
    if (!content) return;
    // Measured from the boxes, not offsetTop: a section mid-entrance (its
    // animation carries a transform) becomes its rows' offsetParent, and
    // offsetTop would count from the section instead of the list.
    const measure = () => {
      const all = rows();
      const index = Math.min(active, all.length - 1);
      all.forEach((row, i) => {
        row.querySelector(OPTION_SELECTOR)?.setAttribute('aria-selected', String(i === index));
      });
      const row = all[index];
      const box = row?.getBoundingClientRect();
      const next = box
        ? {
            top: Math.round(box.top - content.getBoundingClientRect().top),
            height: Math.round(box.height),
          }
        : null;
      setHighlight((prev) => (sameBox(prev, next) ? prev : next));
      setActiveId(row?.querySelector(OPTION_SELECTOR)?.id);
    };
    measure();
    const mutations = new MutationObserver(measure);
    mutations.observe(content, { childList: true, subtree: true });
    const resizes = new ResizeObserver(measure);
    resizes.observe(content);
    // A row rising into place ends where its box finally is.
    content.addEventListener('animationend', measure);
    return () => {
      mutations.disconnect();
      resizes.disconnect();
      content.removeEventListener('animationend', measure);
    };
  }, [active, contentRef, rows]);

  // A key moved the highlight off screen: bring its row, and its section's caption, into view.
  useEffect(() => {
    if (!scrollOnMove.current) return;
    scrollOnMove.current = false;
    const list = listRef.current;
    const row = rows()[active];
    if (!list || !row) return;
    if (active === 0) {
      list.scrollTop = 0;
      return;
    }
    const top = row.getBoundingClientRect().top - list.getBoundingClientRect().top + list.scrollTop;
    const caption = row.previousElementSibling ? 0 : SECTION_CAPTION_PX;
    if (top - caption - SCROLL_MARGIN_PX < list.scrollTop) {
      list.scrollTop = top - caption - SCROLL_MARGIN_PX;
    } else if (top + row.offsetHeight + SCROLL_MARGIN_PX > list.scrollTop + list.clientHeight) {
      list.scrollTop = top + row.offsetHeight + SCROLL_MARGIN_PX - list.clientHeight;
    }
  }, [active, listRef, rows]);

  const move = useCallback(
    (step: 1 | -1) => {
      const count = rows().length;
      if (!count) return;
      scrollOnMove.current = true;
      setActive((index) => (Math.min(index, count - 1) + step + count) % count);
    },
    [rows],
  );

  const activeLink = useCallback(
    () => rows()[active]?.querySelector<HTMLAnchorElement>('a') ?? null,
    [active, rows],
  );

  const handlePointerMove = useCallback(
    (e: PointerEvent<HTMLElement>) => {
      // The list scrolling under a still pointer is not the pointer moving.
      const at = `${e.clientX},${e.clientY}`;
      if (at === lastPointer.current) return;
      lastPointer.current = at;
      const row = e.target instanceof Element ? e.target.closest(SEARCH_ROW_SELECTOR) : null;
      const index = row instanceof HTMLElement ? rows().indexOf(row) : -1;
      if (index >= 0) setActive(index);
    },
    [rows],
  );

  return { highlight, activeId, move, activeLink, handlePointerMove };
}
